// WEB ONLY — native code must import from nativeWorkoutIconMapper.tsx
/**
 * Workout Icon Mapper
 * Maps workout types to icon types and intensity to colors for session cards
 *
 * Uses public directory SVG icons with CSS filters for color changes
 * Intensity determined by LLM in Phase 3 (weekly plan synthesis)
 */

import React from 'react';
import { modalityIconPrecedence, type ExecutionModality } from './executionModality';

export type WorkoutIconType =
  | 'running'
  | 'cycling'
  | 'swimming'
  | 'rowing'
  | 'walking'
  | 'weightlifting'
  | 'bodyweight'
  | 'core'
  | 'crossfit'
  | 'yoga'
  | 'stretching'
  | 'hiit'
  | 'team-sports'
  | 'racquet-sports'
  | 'golf'
  | 'martial-arts'
  | 'plyometrics'
  | 'climbing'
  | 'snow-sports'
  | 'cross-training'
  | 'default';

// ─────────────────────────────────────────────
// Intensity Colors (cardio) — matches nativeWorkoutIconMapper
// ─────────────────────────────────────────────

const INTENSITY_HARD   = '#A6496E'; // Mauve  — intervals, tempo, threshold, race pace
const INTENSITY_MEDIUM = '#E87722'; // Orange — base, progression, long run, steady
const INTENSITY_EASY   = '#1F6C4F'; // Green  — easy, recovery, jog, warmup, cooldown

const CARDIO_TYPES = new Set<WorkoutIconType>(['running', 'cycling', 'swimming', 'rowing', 'walking']);

const HARD_KEYWORDS = [
  'interval', 'intervals', 'tempo', 'threshold', 'race', 'race pace',
  'speed', 'fartlek', 'vo2', 'vo2max', 'track', 'repetition', 'strides',
  'hard', 'sprint', 'all-out', 'anaerobic', 'surge', 'max effort',
  'fast finish', 'lactate', 'hill repeat', 'hill repeats',
];

const EASY_KEYWORDS = [
  'easy', 'recovery', 'jog', 'slow', 'long slow', 'lsd', 'gentle',
  'conversational', 'zone 1', 'zone 2', 'active recovery', 'shakeout',
  'warm-up', 'warmup', 'cooldown', 'cool-down',
];

/**
 * Category color mapping (aligned with native nativeWorkoutIconMapper)
 * Cardio uses intensity-based color (see getWorkoutIntensityColor)
 * 🟣 Purple = Strength (weightlifting, bodyweight, core, crossfit)
 * 🔴 Mauve = HIIT (interval training, circuits, team sports, racquet sports)
 * 🟢 Cedar Light = Yoga/Mobility (yoga, stretching)
 */
const CATEGORY_COLORS: Record<WorkoutIconType, string> = {
  // Cardio — default medium orange (overridden by getWorkoutIntensityColor)
  'running':          INTENSITY_MEDIUM,
  'cycling':          INTENSITY_MEDIUM,
  'swimming':         INTENSITY_MEDIUM,
  'rowing':           INTENSITY_MEDIUM,
  'walking':          INTENSITY_MEDIUM,

  // Strength (Purple)
  'weightlifting':    '#7834b5',
  'bodyweight':       '#7834b5',
  'core':             '#7834b5',
  'crossfit':         '#7834b5',

  // HIIT (Mauve)
  'hiit':             '#A6496E',
  'team-sports':      '#A6496E',
  'racquet-sports':   '#A6496E',
  'martial-arts':     '#A6496E',

  // Yoga / Mobility (Cedar Light)
  'yoga':             '#5FA287',
  'stretching':       '#5FA287',
  'plyometrics':      '#1F6C4F',
  'golf':             '#1F6C4F',
  'climbing':         '#1F6C4F',
  'snow-sports':      '#1F6C4F',
  'cross-training':   '#E87722',

  // Default
  'default': '#1F6C4F'
};

/**
 * Get category color for icon type (non-cardio fallback)
 */
export function getCategoryColor(iconType: WorkoutIconType): string {
  return CATEGORY_COLORS[iconType] || CATEGORY_COLORS['default'];
}

/**
 * Get intensity-based color for cardio workouts, or category color for non-cardio.
 * Matches native nativeWorkoutIconMapper.getWorkoutIntensityColor()
 */
export function getWorkoutIntensityColor(iconType: WorkoutIconType, title?: string): string {
  if (!CARDIO_TYPES.has(iconType)) {
    return getCategoryColor(iconType);
  }
  if (!title) return INTENSITY_MEDIUM;
  const t = title.toLowerCase();
  const hardPos = HARD_KEYWORDS.reduce((min, kw) => {
    const idx = t.indexOf(kw);
    return idx >= 0 && idx < min ? idx : min;
  }, Infinity);
  const easyPos = EASY_KEYWORDS.reduce((min, kw) => {
    const idx = t.indexOf(kw);
    return idx >= 0 && idx < min ? idx : min;
  }, Infinity);
  if (hardPos !== Infinity && easyPos !== Infinity) {
    return easyPos <= hardPos ? INTENSITY_EASY : INTENSITY_HARD;
  }
  if (hardPos !== Infinity) return INTENSITY_HARD;
  if (easyPos !== Infinity) return INTENSITY_EASY;
  return INTENSITY_MEDIUM;
}

/**
 * Map workout type/activityType to icon type.
 *
 * IMPORTANT: Priority order matches nativeWorkoutIconMapper.tsx exactly.
 * "Learn Before You Retrieve" — check TYPE field (backend canonical category)
 * before title-based inference to prevent misclassification
 * (e.g. "Runner Stability Strength" type="full-body-strength" should NOT
 * match running just because the title contains "Runner").
 */
export function getWorkoutIconType(
  workoutType: string,
  activityType?: string,
  executionModality?: ExecutionModality | string | null,
): WorkoutIconType {
  // ── Priority 0 (Jul 2026, legible-modality-grove; refined 2026-07-20):
  // specific modality wins outright; a generic bucket (sport_play → team-sports,
  // other → cross-training) defers to a specific workout_type so a rock-climbing
  // session stamped sport_play renders a climber, not a basketball. Mirrors
  // nativeWorkoutIconMapper — house rule: keep both in lockstep.
  const { icon: modalityIcon, mode: modalityMode } = modalityIconPrecedence(
    executionModality ?? null,
  );
  if (modalityMode === 'override') return modalityIcon as WorkoutIconType;

  const type = (workoutType || '').toLowerCase();
  const activity = (activityType || '').toLowerCase();
  const combined = `${type} ${activity}`.toLowerCase();

  // ── Priority 1: Check the explicit `type` field first ──────────────

  // Stability / core — resolve BEFORE strength so "balance-stability" gets
  // figure.core.training instead of dumbbell.fill.  Must precede the strength
  // block because "core-strength" contains "strength" and would otherwise be
  // caught there and misrouted to 'weightlifting'.
  if (type === 'core' || type === 'core-training' || type.includes('stability'))
    return 'core';

  // Strength / weight training — check TYPE before running
  // Includes all backend types: upper-push, upper-pull, upper-body, lower-body,
  // full-body-strength, powerweight, olympic-lifting, bodyweight-strength, core-strength
  if (
    type.includes('strength') || type.includes('weightlifting') ||
    type.includes('bodyweight') || type.includes('core') ||
    type.includes('upper-push') || type.includes('upper-pull') ||
    type.includes('upper-body') || type.includes('lower-body') ||
    type.includes('push') || type.includes('pull') ||
    type.includes('powerweight') || type.includes('powerlifting') ||
    type.includes('olympic-lifting') || type.includes('lifting')
  ) {
    if (type.includes('bodyweight')) return 'bodyweight';
    return 'weightlifting';
  }

  // HIIT / circuit — check TYPE before running
  if (type.includes('hiit') || type.includes('circuit') || type.includes('interval-training'))
    return 'hiit';

  // Yoga / mobility / stretching — check TYPE before running
  if (type.includes('yoga') || type.includes('vinyasa')) return 'yoga';
  if (type.includes('mobility') || type.includes('stretching') || type.includes('flexibility') ||
      type.includes('foam-rolling') || type.includes('corrective') ||
      type.includes('warmup') || type.includes('warm-up') || type.includes('dynamic-warmup') ||
      type.includes('cooldown') || type.includes('cool-down') ||
      type.includes('movement-prep') || type.includes('prehab') || type.includes('rehab'))
    return 'stretching';

  // Recovery / active-recovery → walking icon
  if (type === 'recovery' || type === 'active-recovery' || type === 'active_recovery' ||
      type === 'sauna-cold')
    return 'walking';

  // Additional backend types that need explicit mapping
  if (type === 'meditation') return 'yoga';
  if (type === 'massage-therapy') return 'stretching';
  if (type === 'paddling') return 'rowing';
  if (type === 'agility-training') return 'plyometrics';
  if (type === 'functional-training') return 'weightlifting';
  if (type === 'sports') return 'team-sports';
  // Stair climbing (Health Connect 'stair_climbing') contains "climbing" but is
  // not the sport — it resolves as Apple's 'stairs' does.
  if (type === 'rock-climbing' || (type.includes('climbing') && !type.includes('stair'))) return 'climbing';
  if (type === 'skiing-snowboarding' || type.includes('skiing') || type.includes('snowboard')) return 'snow-sports';
  if (type === 'general-fitness' || type === 'cross-training') return 'cross-training';

  // ── Priority 2: Cardio types (type field or title) ─────────────────
  // Legacy cardio types (base-cardio, tempo-cardio, etc.) → NEUTRAL icon.
  // 🔧 Jul 2026 (legible-modality-grove): was 'running' — a deliberately
  // non-sport-specific type got the most sport-specific icon. Sport icons
  // now come from executionModality (Priority 0); generic stays generic.
  if (type === 'cardio' || type.endsWith('-cardio'))
    return 'cross-training';

  // A cardio family the TYPE names beats any title word (icon audit 2026-09-25):
  // the checks below are "type OR title" in run → bike → swim → row → walk
  // order, so a walk titled "Barbell Rows" drew a rowing machine and an erg
  // titled "Easy Run" a runner. The type is the backend's category.
  if (type.includes('running')) return 'running';
  if (type.includes('cycling')) return 'cycling';
  if (type.includes('swimming')) return 'swimming';
  if (type.includes('rowing')) return 'rowing';
  if (type.includes('walking') || type.includes('hiking')) return 'walking';

  if (type.includes('running') || activity.includes('running') || combined.includes('run'))
    return 'running';

  if (type.includes('cycling') || activity.includes('cycling') || combined.includes('bike') || combined.includes('bicycle'))
    return 'cycling';

  if (type.includes('swimming') || activity.includes('swimming') || combined.includes('swim'))
    return 'swimming';

  if (type.includes('rowing') || activity.includes('rowing') || combined.includes('row'))
    return 'rowing';

  if (
    type.includes('walking') || activity.includes('walking') ||
    type.includes('hiking') || activity.includes('hiking') ||
    combined.includes('walk') || combined.includes('hike')
  )
    return 'walking';

  // ── Priority 3: Title-based inference for non-type workouts ────────
  // Core before generic strength
  if (combined.includes('core'))
    return 'core';

  if (
    activity.includes('weightlifting') ||
    activity.includes('strength') ||
    combined.includes('weight') || combined.includes('barbell') || combined.includes('dumbbell')
  )
    return 'weightlifting';

  if (
    activity.includes('bodyweight') ||
    combined.includes('calisthenics') || combined.includes('pushup') || combined.includes('plank')
  )
    return 'bodyweight';

  if (
    type.includes('crossfit') || activity.includes('crossfit') ||
    combined.includes('crossfit') || combined.includes('wod')
  )
    return 'crossfit';

  if (type.includes('yoga') || activity.includes('yoga') || combined.includes('yoga'))
    return 'yoga';

  if (
    type.includes('stretching') || activity.includes('stretching') ||
    type.includes('mobility') || activity.includes('mobility') ||
    type.includes('flexibility') || activity.includes('flexibility') ||
    combined.includes('stretch') || combined.includes('mobility')
  )
    return 'stretching';

  if (
    type.includes('hiit') || activity.includes('hiit') ||
    type.includes('interval') || activity.includes('interval') ||
    combined.includes('hiit') || combined.includes('circuit') ||
    combined.includes('emom') || combined.includes('amrap')
  )
    return 'hiit';

  if (
    type.includes('basketball') || activity.includes('basketball') ||
    type.includes('soccer') || activity.includes('soccer') ||
    type.includes('football') || activity.includes('football') ||
    type.includes('volleyball') || activity.includes('volleyball') ||
    type.includes('hockey') || activity.includes('hockey') ||
    type.includes('rugby') || activity.includes('rugby') ||
    type.includes('baseball') || activity.includes('baseball') ||
    combined.includes('team')
  )
    return 'team-sports';

  if (
    type.includes('tennis') || activity.includes('tennis') ||
    type.includes('badminton') || activity.includes('badminton') ||
    type.includes('squash') || activity.includes('squash') ||
    combined.includes('racquet') || combined.includes('racket')
  )
    return 'racquet-sports';

  if (type.includes('golf') || activity.includes('golf') || combined.includes('golf'))
    return 'golf';

  if (
    type.includes('martial') || activity.includes('martial') ||
    type.includes('karate') || activity.includes('karate') ||
    type.includes('boxing') || activity.includes('boxing') ||
    combined.includes('martial')
  )
    return 'martial-arts';

  if (
    type.includes('plyometric') || activity.includes('plyometric') ||
    combined.includes('plyo') || combined.includes('jump')
  )
    return 'plyometrics';

  // Generic-modality floor: a sport_play/other session with no workout_type
  // signal still reads as its bucket icon rather than a bare default.
  return modalityMode === 'defer' && modalityIcon
    ? (modalityIcon as WorkoutIconType)
    : 'default';
}

/**
 * Workout Icon Component
 * Uses SVG icons with category-based coloring (aligned with pill colors)
 * 
 * Icon files: /activity-icons/{iconType}.svg
 * Colors: Teal (cardio) | Purple (strength) | Red (HIIT) | Cedar Green (mobility)
 * 
 * @param iconType OR type - The type of workout icon to display
 * @param size - Icon size in pixels (default: 32px for cards, 16px for day dots)
 * @param className - Additional CSS classes
 */
export function WorkoutIcon({
  iconType,
  type,
  title,
  size = 32,
  className = ''
}: {
  iconType?: WorkoutIconType;
  type?: WorkoutIconType; // Alias for iconType
  title?: string; // Used for intensity-based cardio coloring
  size?: number;
  className?: string;
}) {
  // Support both iconType and type props (backward compatibility)
  const actualIconType = iconType || type || 'default';

  // Use BASE_URL so /activity-icons/*.svg resolve in subpath deployments (e.g. /app/)
  const base = (typeof import.meta !== 'undefined' && (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL) || '/';
  const basePath = base.replace(/\/$/, '') || '/';
  const iconPath = `${basePath}/activity-icons/${actualIconType}.svg`;
  const color = getWorkoutIntensityColor(actualIconType, title);

  const [iconSrc, setIconSrc] = React.useState(iconPath);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    setIconSrc(iconPath);
    setHasError(false);
  }, [iconPath]);

  const defaultPath = `${basePath}/activity-icons/default.svg`;

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Prevent infinite loop: only fallback once, and only if not already on default
    const currentSrc = e.currentTarget.src;
    
    if (!hasError && !currentSrc.includes('default.svg')) {
      setHasError(true);
      // Use setTimeout to break the error loop
      setTimeout(() => {
        setIconSrc(defaultPath);
      }, 0);
    }
  };

  // Dynamic size classes
  const sizeStyle = { width: `${size}px`, height: `${size}px` };

  // 🎨 JAN 21, 2026: Apply category color using CSS mask
  // SVG loaded as background, mask applied, then colored with backgroundColor
  return (
    <div 
      className={`flex-shrink-0 ${className}`}
      style={{
        ...sizeStyle,
        backgroundColor: color,
        WebkitMaskImage: `url(${iconSrc})`,
        maskImage: `url(${iconSrc})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center'
      }}
    />
  );
}

